import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    addCurrentMemberChild,
    deleteCurrentMemberChild,
    fetchCurrentMember,
    mapApiChildToView,
    updateCurrentMember,
    updateCurrentMemberChild,
    updateSubMember,
    uploadCurrentMemberProfilePicture,
} from '../api/membersDirectory';
import {
    MemberDetailContent,
    MemberDetailPageSkeleton,
} from '../components/MemberDetailContent';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { resolvePublicMediaUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { MEMBERS, findClubMemberForDetailId } from '../data/members';
import { mapClubMemberToDetailView } from '../utils/memberDetailView';
import { truncateMetaDescription } from '../utils/metaText';

/**
 * @param {import('../context/AuthContext').AuthContextValue['user']} user
 */
function resolveSimulatedProfile(user) {
    if (user?.memberId) {
        const resolved = findClubMemberForDetailId(user.memberId);
        if (resolved) {
            return mapClubMemberToDetailView(resolved.record, {
                viewAsPartner: resolved.viewAsPartner,
            });
        }
    }

    const email = user?.email?.trim().toLowerCase();
    if (email) {
        const byEmail = MEMBERS.find(
            (m) => (m.email || '').trim().toLowerCase() === email,
        );
        if (byEmail) {
            return mapClubMemberToDetailView(byEmail);
        }
    }

    const name = String(user?.name || user?.email || '').trim();
    return {
        id: user?.memberId ? String(user.memberId) : '',
        fullName: name,
        email: String(user?.email || '').trim(),
        phoneNumber: String(user?.phoneNumber || '').trim(),
        jobTitle: '',
        company: '',
        sector: '',
        university: null,
        highSchool: null,
        address: '',
        workAddress: '',
        profilePictureUrl: user?.profilePictureUrl
            ? resolvePublicMediaUrl(String(user.profilePictureUrl))
            : '',
        birthDate: null,
        clubJoinDate: null,
        website: null,
        hobbies: null,
        facebook: null,
        twitter: null,
        linkedIn: null,
        membershipType: null,
        referenceMemberId: null,
        isPartnerProfile: false,
        spouses: [],
        children: [],
        groupLeader: false,
        partnerGivenName: undefined,
        partnerFamilyName: undefined,
        partnerProfilePictureUrl: undefined,
    };
}

/**
 * Oturum açmış üyenin kendi profili — `/profile`
 */
export function ProfilePage() {
    const { t } = useTranslation();
    const { user, authToken, isSimulatedAuth, updateUser } = useAuth();
    const [member, setMember] = useState(
        /** @type {ReturnType<typeof mapClubMemberToDetailView> | null} */ (
            null
        ),
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(/** @type {string | null} */ (null));
    const [photoUploading, setPhotoUploading] = useState(false);
    const [childSubmitting, setChildSubmitting] = useState(false);
    const [deletingChildId, setDeletingChildId] = useState(
        /** @type {string | null} */ (null),
    );
    const [spouseSubmitting, setSpouseSubmitting] = useState(false);
    const [profileSubmitting, setProfileSubmitting] = useState(false);

    /** Profil fotoğrafı güncellemesi `user` nesnesini değiştirir; tam `user` bağımlılığı GET /me ile eski fotoğrafı geri yükler. */
    const simulationUserKey = isSimulatedAuth
        ? `${user?.email ?? ''}|${user?.memberId ?? ''}`
        : '';

    const handlePhotoSelected = useCallback(
        async (file) => {
            if (!file.type.startsWith('image/')) {
                toast.error(t('profilePage.photoInvalidType'));
                return;
            }

            if (isSimulatedAuth) {
                const previewUrl = URL.createObjectURL(file);
                setMember((prev) =>
                    prev
                        ? { ...prev, profilePictureUrl: previewUrl }
                        : prev,
                );
                toast.info(t('profilePage.photoSimulationNotice'));
                return;
            }

            if (!authToken) {
                toast.error(t('profilePage.sessionRequired'));
                return;
            }

            const previewUrl = URL.createObjectURL(file);
            let previousPhotoUrl = '';
            setMember((prev) => {
                previousPhotoUrl = prev?.profilePictureUrl || '';
                return prev
                    ? { ...prev, profilePictureUrl: previewUrl }
                    : prev;
            });

            setPhotoUploading(true);
            try {
                const { message, profilePictureUrl } =
                    await uploadCurrentMemberProfilePicture(authToken, file);
                const baseUrl = resolvePublicMediaUrl(profilePictureUrl);
                const displayUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
                setMember((prev) =>
                    prev
                        ? { ...prev, profilePictureUrl: displayUrl }
                        : prev,
                );
                updateUser({ profilePictureUrl: displayUrl });
                toast.success(message || t('profilePage.photoUploadSuccess'), {
                    autoClose: 2800,
                });
            } catch (e) {
                setMember((prev) =>
                    prev
                        ? {
                              ...prev,
                              profilePictureUrl: previousPhotoUrl,
                          }
                        : prev,
                );
                toast.error(
                    e instanceof Error
                        ? e.message
                        : t('profilePage.photoUploadError'),
                );
            } finally {
                URL.revokeObjectURL(previewUrl);
                setPhotoUploading(false);
            }
        },
        [authToken, isSimulatedAuth, t, updateUser],
    );

    const handleAddChild = useCallback(
        async (payload) => {
            if (isSimulatedAuth) {
                const child = mapApiChildToView({
                    id: `sim-${Date.now()}`,
                    firstName: payload.firstName,
                    lastName: payload.lastName,
                    birthDate: payload.birthDate,
                });
                setMember((prev) =>
                    prev
                        ? {
                              ...prev,
                              children: [
                                  ...(Array.isArray(prev.children)
                                      ? prev.children
                                      : []),
                                  child,
                              ],
                          }
                        : prev,
                );
                toast.success(t('profilePage.addChildSuccess'));
                return;
            }

            if (!authToken) {
                throw new Error(t('profilePage.sessionRequired'));
            }

            setChildSubmitting(true);
            try {
                const { message, child } = await addCurrentMemberChild(
                    authToken,
                    payload,
                );
                setMember((prev) =>
                    prev
                        ? {
                              ...prev,
                              children: [
                                  ...(Array.isArray(prev.children)
                                      ? prev.children
                                      : []),
                                  child,
                              ],
                          }
                        : prev,
                );
                toast.success(
                    message || t('profilePage.addChildSuccess'),
                    { autoClose: 2800 },
                );
            } finally {
                setChildSubmitting(false);
            }
        },
        [authToken, isSimulatedAuth, t],
    );

    const handleUpdateChild = useCallback(
        async (payload) => {
            const id = String(payload.id || '').trim();
            if (!id) return;

            if (isSimulatedAuth) {
                const child = mapApiChildToView({
                    id,
                    firstName: payload.firstName,
                    lastName: payload.lastName,
                    birthDate: payload.birthDate,
                });
                setMember((prev) =>
                    prev
                        ? {
                              ...prev,
                              children: (
                                  Array.isArray(prev.children)
                                      ? prev.children
                                      : []
                              ).map((c) =>
                                  String(c.id) === id ? child : c,
                              ),
                          }
                        : prev,
                );
                toast.success(t('profilePage.editChildSuccess'));
                return;
            }

            if (!authToken) {
                throw new Error(t('profilePage.sessionRequired'));
            }

            setChildSubmitting(true);
            try {
                const { message, child } = await updateCurrentMemberChild(
                    authToken,
                    payload,
                );
                setMember((prev) =>
                    prev
                        ? {
                              ...prev,
                              children: (
                                  Array.isArray(prev.children)
                                      ? prev.children
                                      : []
                              ).map((c) =>
                                  String(c.id) === id ? child : c,
                              ),
                          }
                        : prev,
                );
                toast.success(
                    message || t('profilePage.editChildSuccess'),
                    { autoClose: 2800 },
                );
            } finally {
                setChildSubmitting(false);
            }
        },
        [authToken, isSimulatedAuth, t],
    );

    const handleUpdateProfile = useCallback(
        async (payload) => {
            if (isSimulatedAuth) {
                const firstName = payload.firstName;
                const lastName = payload.lastName;
                setMember((prev) =>
                    prev
                        ? {
                              ...prev,
                              firstName,
                              lastName,
                              fullName: [firstName, lastName]
                                  .filter(Boolean)
                                  .join(' '),
                              email: payload.email,
                              phoneNumber: payload.phoneNumber,
                              jobTitle: payload.jobTitle,
                              company: payload.company,
                              sector: payload.sector,
                              university: payload.university || null,
                              highSchool: payload.highSchool || null,
                              address: payload.address,
                              workAddress: payload.workAddress,
                              tcKimlikNo: payload.tcKimlikNo,
                              birthDate: payload.birthDate || null,
                              clubJoinDate: payload.clubJoinDate || null,
                              website: payload.website || null,
                              hobbies: payload.hobbies || null,
                              facebook: payload.facebook || null,
                              twitter: payload.twitter || null,
                              linkedIn: payload.linkedIn || null,
                          }
                        : prev,
                );
                updateUser({
                    name: [firstName, lastName].filter(Boolean).join(' '),
                    email: payload.email,
                    phoneNumber: payload.phoneNumber,
                });
                toast.success(t('profilePage.editProfileSuccess'));
                return;
            }

            if (!authToken) {
                throw new Error(t('profilePage.sessionRequired'));
            }

            setProfileSubmitting(true);
            try {
                const { message, member: updated } = await updateCurrentMember(
                    authToken,
                    payload,
                );
                setMember((prev) => ({
                    ...updated,
                    profilePictureUrl:
                        prev?.profilePictureUrl ?? updated.profilePictureUrl,
                }));
                updateUser({
                    name: updated.fullName,
                    email: updated.email,
                    phoneNumber: updated.phoneNumber,
                });
                toast.success(
                    message || t('profilePage.editProfileSuccess'),
                    { autoClose: 4000 },
                );
            } finally {
                setProfileSubmitting(false);
            }
        },
        [authToken, isSimulatedAuth, t, updateUser],
    );

    const handleUpdateSpouse = useCallback(
        async (subMemberId, payload) => {
            const id = String(subMemberId || '').trim();
            if (!id) return;

            if (isSimulatedAuth) {
                setMember((prev) => {
                    if (!prev) return prev;
                    const spouses = (
                        Array.isArray(prev.spouses) ? prev.spouses : []
                    ).map((s) => {
                        if (String(s.id) !== id) return s;
                        const firstName = payload.firstName;
                        const lastName = payload.lastName;
                        return {
                            ...s,
                            firstName,
                            lastName,
                            fullName: [firstName, lastName]
                                .filter(Boolean)
                                .join(' '),
                            email: payload.email,
                            phoneNumber: payload.phoneNumber,
                            jobTitle: payload.jobTitle,
                            company: payload.company,
                            sector: payload.sector,
                            university: payload.university,
                            highSchool: payload.highSchool,
                            address: payload.address,
                            workAddress: payload.workAddress,
                            tcKimlikNo: payload.tcKimlikNo,
                            birthDate: payload.birthDate || null,
                            clubJoinDate: payload.clubJoinDate || null,
                            website: payload.website,
                            hobbies: payload.hobbies,
                            facebook: payload.facebook,
                            twitter: payload.twitter,
                            linkedIn: payload.linkedIn,
                        };
                    });
                    return { ...prev, spouses };
                });
                toast.success(t('profilePage.editSpouseSuccess'));
                return;
            }

            if (!authToken) {
                throw new Error(t('profilePage.sessionRequired'));
            }

            setSpouseSubmitting(true);
            try {
                const { message, spouses } = await updateSubMember(
                    authToken,
                    id,
                    payload,
                );
                setMember((prev) =>
                    prev ? { ...prev, spouses } : prev,
                );
                toast.success(
                    message || t('profilePage.editSpouseSuccess'),
                    { autoClose: 4000 },
                );
            } finally {
                setSpouseSubmitting(false);
            }
        },
        [authToken, isSimulatedAuth, t],
    );

    const handleDeleteChild = useCallback(
        async (childId) => {
            const id = String(childId || '').trim();
            if (!id) return;

            if (isSimulatedAuth) {
                setMember((prev) =>
                    prev
                        ? {
                              ...prev,
                              children: (
                                  Array.isArray(prev.children)
                                      ? prev.children
                                      : []
                              ).filter((c) => String(c.id) !== id),
                          }
                        : prev,
                );
                toast.success(t('profilePage.removeChildSuccess'));
                return;
            }

            if (!authToken) {
                toast.error(t('profilePage.sessionRequired'));
                return;
            }

            setDeletingChildId(id);
            try {
                const { message } = await deleteCurrentMemberChild(
                    authToken,
                    id,
                );
                setMember((prev) =>
                    prev
                        ? {
                              ...prev,
                              children: (
                                  Array.isArray(prev.children)
                                      ? prev.children
                                      : []
                              ).filter((c) => String(c.id) !== id),
                          }
                        : prev,
                );
                toast.success(
                    message || t('profilePage.removeChildSuccess'),
                    { autoClose: 2800 },
                );
            } catch (e) {
                toast.error(
                    e instanceof Error
                        ? e.message
                        : t('profilePage.removeChildError'),
                );
            } finally {
                setDeletingChildId(null);
            }
        },
        [authToken, isSimulatedAuth, t],
    );

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            setMember(null);

            try {
                if (isSimulatedAuth) {
                    if (cancelled) return;
                    setMember(resolveSimulatedProfile(user));
                } else {
                    if (!authToken) {
                        throw new Error(t('profilePage.sessionRequired'));
                    }
                    const data = await fetchCurrentMember(authToken);
                    if (cancelled) return;
                    setMember(data);
                }
            } catch (e) {
                if (cancelled) return;
                setError(
                    e instanceof Error
                        ? e.message
                        : t('profilePage.loadError'),
                );
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    // `user` bilinçle bağımlılık dışı — profil fotoğrafı güncellemesi GET /me ile ezilmesin
    }, [authToken, isSimulatedAuth, simulationUserKey, t]);

    if (loading) {
        return (
            <>
                <PageSeo
                    title={`${t('profilePage.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.profile')}
                    noindex
                />
                <SubpageLayout
                    title={t('profilePage.title')}
                    contentClassName="member-detail-page"
                >
                    <MemberDetailPageSkeleton />
                </SubpageLayout>
            </>
        );
    }

    if (error || !member) {
        return (
            <>
                <PageSeo
                    title={`${t('profilePage.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.profile')}
                    noindex
                />
                <SubpageLayout
                    title={t('profilePage.title')}
                    contentClassName="member-detail-page"
                >
                    <p className="member-detail-placeholder mb-0 text-secondary">
                        {error || t('profilePage.loadError')}
                    </p>
                </SubpageLayout>
            </>
        );
    }

    const nameForTitle = member.fullName?.trim() || '—';
    const profileMetaDesc = truncateMetaDescription(
        [member.jobTitle, member.company].filter(Boolean).join(' · ') ||
            t('seo.profile'),
    );

    return (
        <>
            <PageSeo
                title={`${nameForTitle} — ${t('profilePage.title')}`}
                description={profileMetaDesc}
                noindex
                ogImage={
                    member.profilePictureUrl
                        ? resolvePublicMediaUrl(member.profilePictureUrl)
                        : undefined
                }
            />
            <SubpageLayout
                title={t('profilePage.title')}
                contentClassName="member-detail-page"
            >
                <MemberDetailContent
                    member={member}
                    spouseLinkFromProfile
                    photoEditable
                    photoUploading={photoUploading}
                    onPhotoSelected={handlePhotoSelected}
                    childrenEditable
                    childSubmitting={childSubmitting}
                    onAddChild={handleAddChild}
                    onUpdateChild={handleUpdateChild}
                    onDeleteChild={handleDeleteChild}
                    deletingChildId={deletingChildId}
                    spouseEditable
                    spouseSubmitting={spouseSubmitting}
                    onUpdateSpouse={handleUpdateSpouse}
                    profileEditable
                    profileSubmitting={profileSubmitting}
                    onUpdateProfile={handleUpdateProfile}
                />
            </SubpageLayout>
        </>
    );
}
